import React, { useState, useEffect, useRef } from "react";
import { createChart } from 'lightweight-charts';
import { useTheme } from '@mui/material';

const CHART_COLORS = {
  GOLD: '#95fe51',
  TRENDS: '#0296E5',
  VIX: '#DA9551',
  DXY: '#E05920',
  US10YY: '#009BA0'
};

// ================= SKELETON COMPONENTS =================

const shimmerCSS = `
@keyframes goldShimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
`;

function ChartSkeleton({ height, watermark = "", delay = 0 }) {
  return (
    <div
      style={{
        width: '100%',
        height,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: '#0D2233',
        position: 'relative',
        border: '2px solid #000',
        boxShadow: '0px 2px 5px rgba(255,255,255,0.5)',
      }}
    >
      {/* fake grid lines */}
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        preserveAspectRatio="none"
      >
        {/* horizontal */}
        {[20, 38, 56, 74].map((pct, i) => (
          <line key={`h${i}`} x1="0" y1={`${pct}%`} x2="100%" y2={`${pct}%`} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
        ))}
        {/* vertical */}
        {[16, 33, 50, 67, 84].map((pct, i) => (
          <line key={`v${i}`} x1={`${pct}%`} y1="0" x2={`${pct}%`} y2="100%" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
        ))}
        {/* fake price line — unique per chart via watermark seed */}
        <polyline
          points={watermark === 'Gold (COMEX)'
            ? "0,220 60,200 120,210 200,160 280,175 360,140 440,155 520,120 600,135 700,100 800,115 900,90 1000,105 1100,80 1200,95 1300,70"
            : watermark === 'Trends'
            ? "0,180 100,160 200,175 300,140 400,155 500,120 600,140 700,110 800,130 900,100 1000,120"
            : watermark === 'VIX'
            ? "0,120 100,140 200,110 300,150 400,125 500,145 600,115 700,135 800,108 900,128"
            : watermark === 'DXY'
            ? "0,100 100,115 200,95 300,120 400,100 500,130 600,105 700,120 800,90 900,110"
            : "0,130 100,115 200,140 300,110 400,135 500,105 600,125 700,95 800,115 900,88"
          }
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* watermark label */}
      {watermark && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'rgba(255,255,255,0.05)', fontSize: 30, fontWeight: 'bold',
          pointerEvents: 'none', userSelect: 'none',
        }}>
          {watermark}
        </div>
      )}

      {/* shimmer sweep */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.06) 40%, rgba(125,211,252,0.12) 50%, rgba(56,189,248,0.06) 60%, transparent 100%)',
        animation: `goldShimmer 1.8s ease-in-out ${delay}s infinite`,
      }} />
    </div>
  );
}

// ================= MAIN COMPONENT =================

export default function FearAndGreed() {
  const theme = useTheme();

  // --- Chart Refs & Data States ---
  const chartContainerRef  = useRef(null);
  const chartContainerRef2 = useRef(null);
  const chartContainerRef3 = useRef(null);
  const chartContainerRef4 = useRef(null);
  const chartContainerRef5 = useRef(null);

  const [data,  setData]  = useState([]);
  const [data2, setData2] = useState([]);
  const [data3, setData3] = useState([]);
  const [data4, setData4] = useState([]);
  const [data5, setData5] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  /* ================= DATA FETCHING ================= */
  const uri = window.config?.base_api_url || "";

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetch(`${uri}/fearandgreed?symbol=fg`).then(r => r.json()).then(d => setData(d.data)),
      fetch(`${uri}/fearandgreed?symbol=set`).then(r  => r.json()).then(d => setData2(d.data)),
      fetch(`${uri}/fearandgreed?symbol=sp500`).then(r   => r.json()).then(d => setData3(d.data)),
      fetch(`${uri}/fearandgreed?symbol=nasdaq`).then(r   => r.json()).then(d => setData4(d.data)),
      fetch(`${uri}/fearandgreed?symbol=hsi`).then(r => r.json()).then(d => setData5(d.data)),
    ]).catch(console.error);
  }, [uri]);

  /* ================= CHART CREATION & SYNC ================= */
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const charts = [];
    const seriesList = [];

    function syncCrosshair(chart, series, dataPoint) {
      if (dataPoint) {
        chart.setCrosshairPosition(dataPoint.value, dataPoint.time, series);
      } else {
        chart.clearCrosshairPosition();
      }
    }

    function getCrosshairDataPoint(series, param) {
      if (!param.time) return null;
      return param.seriesData.get(series) || null;
    }

    function createChartWithOptions(containerRef, chartData, watermarkText, color, rightPriceScale = true) {
      if (!chartData || chartData.length === 0) return null;
      if (containerRef.current) containerRef.current.innerHTML = '';

      const commonOptions = {
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
        timeScale: { rightOffset: 10 },
        layout: {
          attributionLogo: false,
          background: { type: 'solid', color: theme.palette.background.default },
          textColor: theme.palette.text.primary,
        },
        grid: {
          horzLines: { color: theme.palette.divider },
          vertLines: { color: theme.palette.divider },
        },
      };

      const chart = createChart(containerRef.current, commonOptions);
      chart.priceScale('right').applyOptions({ visible: rightPriceScale });
      chart.priceScale('left').applyOptions({ visible: false });
      chart.applyOptions({
        watermark: {
          visible: true, fontSize: 30,
          horzAlign: 'center', vertAlign: 'center',
          color: theme.palette.chart.watermark,
          text: watermarkText, fontFamily: 'Lato, Sans-serif',
        },
      });

      const lineSeries = chart.addLineSeries({ color, lineWidth: 2, priceLineVisible: false });
      const formattedData = chartData.map(item => ({
        time: item.time,
        value: typeof item.value === "string" ? parseFloat(item.value) || 0 : item.value,
      }));
      lineSeries.applyOptions({
        priceFormat: {
          type: 'custom',
          formatter: (price) => price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }
      });
      lineSeries.setData(formattedData);

      const resizeObserver = new ResizeObserver(() => {
        if (containerRef.current) {
          chart.applyOptions({
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight,
          });
        }
      });
      resizeObserver.observe(containerRef.current);

      return { chart, lineSeries };
    }

    const getCommonDates = (d1, d2, d3, d4, d5) => {
      const dates2 = new Set(d2.map(item => item.time));
      const dates3 = new Set(d3.map(item => item.time));
      const dates4 = new Set(d4.map(item => item.time));
      const dates5 = new Set(d5.map(item => item.time));
      return d1.map(item => item.time).filter(date =>
        dates2.has(date) && dates3.has(date) && dates4.has(date) && dates5.has(date)
      );
    };

    if (data.length > 0 && data4.length > 0) {
      const commonDates = getCommonDates(data, data2, data3, data4, data5);
      const filterData  = (arr) => arr.filter(item => commonDates.includes(item.time));

      const chart1 = createChartWithOptions(chartContainerRef,  filterData(data),  '', CHART_COLORS.GOLD,   true);
      const chart2 = createChartWithOptions(chartContainerRef2, filterData(data2), 'SET',       CHART_COLORS.TRENDS, true);
      const chart3 = createChartWithOptions(chartContainerRef3, filterData(data3), 'S&P500',          CHART_COLORS.VIX,    true);
      const chart4 = createChartWithOptions(chartContainerRef4, filterData(data4), 'NASDAQ',          CHART_COLORS.DXY,    true);
      const chart5 = createChartWithOptions(chartContainerRef5, filterData(data5), 'HSI',       CHART_COLORS.US10YY, true);

      if (chart1) { charts.push(chart1.chart); seriesList.push(chart1.lineSeries); }
      if (chart2) { charts.push(chart2.chart); seriesList.push(chart2.lineSeries); }
      if (chart3) { charts.push(chart3.chart); seriesList.push(chart3.lineSeries); }
      if (chart4) { charts.push(chart4.chart); seriesList.push(chart4.lineSeries); }
      if (chart5) { charts.push(chart5.chart); seriesList.push(chart5.lineSeries); }

      charts.forEach((chart, index) => {
        chart.subscribeCrosshairMove(param => {
          const dataPoint = getCrosshairDataPoint(seriesList[index], param);
          charts.forEach((otherChart, otherIndex) => {
            if (otherChart !== chart) syncCrosshair(otherChart, seriesList[otherIndex], dataPoint);
          });
        });
        chart.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
          charts.forEach(otherChart => {
            if (otherChart !== chart) otherChart.timeScale().setVisibleLogicalRange(timeRange);
          });
        });
      });

      setIsLoading(false);
    }

    return () => {
      charts.forEach(chart => chart.remove());
    };
  }, [data, data2, data3, data4, data5, theme]);

  /* ================= CHART BOX STYLE ================= */
  const chartBoxStyle = "w-full rounded-[12px] overflow-hidden shadow-[0px_2px_5px_rgba(255,255,255,0.5)] border-2 border-black relative bg-[#0D2233]";

  /* ================= RENDER ================= */
  return (
    <div className="w-full min-h-screen bg-[#0b111a] text-white px-6 py-6 animate-fade-in">
      <style>{shimmerCSS}</style>
      <div className="max-w-[1700px] mx-auto">

        {/* ================= STAT STRIP ================= */}
        <div style={{ display: 'none' }} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 mt-2">
          <StatBox title="GOLD SPOT"       value="2,034.50" change="+24.0 (+0.66%)" positive />
          <StatBox title="GOLD THAI (96.5%)" value="34,550" change="+50"            positive />
          <StatBox title="SILVER"          value="22.85"    change="-0.12 (-0.5%)" />
          <StatBox title="THB/USD"         value="35.60"    change="-0.10 (Stronger)" />
        </div>

        {/* ================= MAIN CHART (GOLD) ================= */}
        {/* chart container mount ตลอด, skeleton ซ้อนทับด้วย absolute */}
        <div className="mb-6 relative">
          {isLoading && (
            <div className="absolute inset-0 z-10">
              <ChartSkeleton height={320} watermark="Gold (COMEX)" delay={0} />
            </div>
          )}
          <div
            className={`${chartBoxStyle} h-[320px]`}
            style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
          >
            <div className="absolute inset-0 w-full h-full" ref={chartContainerRef} />
          </div>
        </div>

        {/* ================= LOWER GRID (4 CHARTS) ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[
            { ref: chartContainerRef2, watermark: 'Trends', delay: 0.15 },
            { ref: chartContainerRef3, watermark: 'VIX',    delay: 0.30 },
            { ref: chartContainerRef4, watermark: 'DXY',    delay: 0.45 },
            { ref: chartContainerRef5, watermark: 'US10YY', delay: 0.60 },
          ].map(({ ref, watermark, delay }) => (
            <div key={watermark} className="relative">
              {isLoading && (
                <div className="absolute inset-0 z-10">
                  <ChartSkeleton height={280} watermark={watermark} delay={delay} />
                </div>
              )}
              <div
                className={`${chartBoxStyle} h-[280px]`}
                style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
              >
                <div className="absolute inset-0 w-full h-full" ref={ref} />
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

// Sub-components
function StatBox({ title, value, change, positive }) {
  return (
    <div className="bg-[#111827] border border-slate-700 rounded-lg px-4 py-3 flex items-center justify-between shadow-sm">
      <span className="text-xs text-slate-400 font-medium">{title}</span>
      <span className="text-base font-bold text-white tracking-wide">{value}</span>
      <span className={`text-xs font-medium ${positive ? "text-green-400" : "text-red-400"}`}>{change}</span>
    </div>
  );
}